'use client';

import { useMemo } from 'react';
import { useAppSelector } from '@akinon/next/redux/hooks';
import { CATEGORY_FACET_KEY } from './filters/category-filter-settings';
import { WIDGET_TYPE } from '@theme/types';

export const FACET_GROUPING = [
  {
    groupName: 'Malzeme',
    keyPattern: '^malzeme_',
    newKey: 'malzeme',
    widgetType: 'multiple_selection'
  },
  {
    groupName: 'Yatış Pozisyonu',
    keyPattern: '^yatis_pozisyonu_',
    newKey: 'yatis_pozisyonu',
    widgetType: 'multiple_selection'
  },
  {
    groupName: 'Ürün Özelliği',
    keyPattern: '^urun_ozelligi_',
    newKey: 'urun_ozelligi',
    widgetType: 'multiple_selection'
  }
];

export const groupFiltersByKeys = (filters, groupKeys) => {
  const groupedFilters = [];

  groupKeys.forEach(({ groupName, keyPattern, newKey, widgetType }) => {
    const regex = new RegExp(keyPattern);

    const groupedChoices = filters
      .filter((item) => regex.test(item.key))
      .flatMap((item) =>
        item.data.choices.map((choice) => ({
          ...choice,
          label: item.name.replace(groupName, '').trim(),
          parent_key: item.key,
          search_key: item.search_key
        }))
      );

    if (groupedChoices.length > 0) {
      groupedFilters.push({
        name: groupName,
        key: newKey,
        search_key: `attributes_${newKey}`,
        widget_type: widgetType,
        order: null,
        extra_params: {},
        data: {
          widget_type: widgetType,
          name: groupName,
          key: newKey,
          search_key: `attributes_${newKey}`,
          choices: groupedChoices,
          order: null
        }
      });
    }
  });

  const groupedKeysRegex = new RegExp(
    groupKeys.map(({ keyPattern }) => keyPattern).join('|')
  );
  const otherFilters = filters.filter(
    (item) => !groupedKeysRegex.test(item.key)
  );

  return [...otherFilters, ...groupedFilters];
};

/**
 * Reads the raw facets from the (single source of truth) category store and
 * returns the grouped view. The store intentionally stays raw so that the
 * middleware-derived `selectedFacets` (pills, filter removal) keep matching the
 * real, ungrouped URL query params. Grouping is a pure view concern computed
 * once here and shared by every consumer.
 */
/**
 * Tek seçenekli facet'ler filtre olarak anlamsız olduğu için gizlenir. Kategori
 * facet'i istisna: bir kategori seçiliyken servis tek seçenek döndürebiliyor ve
 * kullanıcının seçimi kaldırabilmesi için o seçeneğin görünmesi gerekiyor.
 */
const hasVisibleChoices = (facet) => {
  const choiceCount = facet?.data?.choices?.length ?? 0;

  return facet?.key === CATEGORY_FACET_KEY ? choiceCount > 0 : choiceCount > 1;
};

export const useGroupedFacets = () => {
  const facets = useAppSelector((state) => state.category.facets);

  return useMemo(
    () => groupFiltersByKeys(facets, FACET_GROUPING).filter(hasVisibleChoices),
    [facets]
  );
};

export const useTrackableFacets = () => {
  const facets = useAppSelector((state) => state.category.facets);

  return useMemo(() => groupFiltersByKeys(facets, FACET_GROUPING), [facets]);
};

/**
 * `multiple_selection` facet'lerinde her seçenek kendi ham facet'inin
 * `search_key`'ini taşır (bkz. groupFiltersByKeys); diğerlerinde facet'in kendi
 * anahtarı kullanılır.
 */
export const getChoiceSearchKey = (facet, choice) =>
  facet.widget_type === WIDGET_TYPE.multiple_selection
    ? choice.search_key
    : facet.search_key;

export const isMultiSelectFacet = (facet) =>
  facet.widget_type === WIDGET_TYPE.multiselect ||
  facet.widget_type === WIDGET_TYPE.multiple_selection;

export const isChoiceSelected = (facet, choice, params: URLSearchParams) => {
  const searchKey = getChoiceSearchKey(facet, choice);
  const value = String(choice.value);

  return isMultiSelectFacet(facet)
    ? params.getAll(searchKey).includes(value)
    : params.get(searchKey) === value;
};

export const getActiveFilterLabels = (facets, params: URLSearchParams) =>
  facets.flatMap((facet) =>
    (facet.data?.choices ?? [])
      .filter((choice) => isChoiceSelected(facet, choice, params))
      .map((choice) => choice.label)
  );

export const findFilterLabels = (facets, searchKey, value) => {
  for (const facet of facets) {
    for (const choice of facet.data?.choices ?? []) {
      if (
        getChoiceSearchKey(facet, choice) === searchKey &&
        String(choice.value) === String(value)
      ) {
        return { category: facet.name, value: choice.label };
      }
    }
  }

  return null;
};
